import { useState, useEffect, useRef, useCallback } from "react";
import { MessageCircle, X, Send, Trash2, Pin, ChevronDown, ChevronUp, Loader2, GripVertical, AlertTriangle, Filter, AtSign, CheckCircle2, Mail, Reply } from "lucide-react";
import { useTemplateId, useRelativePath } from "./TemplateContext";
import { useContentEditingSafe } from "./ContentEditingContext";
import { listUsers, type UserRecord } from "../../utils/userApi";
import { apiFetch } from "../../utils/apiClient";

const AUTHOR_KEY = "site-comment-author";
const DRAG_THRESHOLD = 5; // px — below this it's a click, above it's a drag

interface Comment {
  id: string;
  x: number; // percentage from right (RTL)
  y: number; // percentage of content height (0-100)
  text: string;
  author: string;
  timestamp: number;
  page: string;
  resolved: boolean;
  /** Site identifier — isolates comments per cloned site */
  siteId?: string;
  /** Mentioned usernames (extracted from @mentions in text) */
  mentions?: string[];
  /** Nested replies */
  replies?: CommentReply[];
}

interface CommentReply {
  id: string;
  text: string;
  author: string;
  timestamp: number;
}

interface DragState {
  commentId: string;
  startMouseX: number;
  startMouseY: number;
  currentX: number; // viewport px
  currentY: number; // viewport px
  isDragging: boolean; // true once threshold exceeded
}

function generateId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

function formatTime(ts: number) {
  const d = new Date(ts);
  const day = d.getDate().toString().padStart(2, "0");
  const month = (d.getMonth() + 1).toString().padStart(2, "0");
  const hours = d.getHours().toString().padStart(2, "0");
  const minutes = d.getMinutes().toString().padStart(2, "0");
  return `${day}/${month} ${hours}:${minutes}`;
}

/**
 * Filter comments to only include ones belonging to this site.
 * Comments without siteId are considered to belong to "default" site
 * (backward compatibility for comments created before siteId was added).
 */
function filterBySite(comments: Comment[], currentSiteId: string): Comment[] {
  return comments.filter((c) => {
    if (c.siteId) return c.siteId === currentSiteId;
    // Legacy comments (no siteId) belong to "default"
    return currentSiteId === "default";
  });
}

/**
 * Migrate old comments that stored y as absolute pixels (> 100)
 * to percentage-based positioning. Uses current content height for conversion.
 */
function migrateOldComments(comments: Comment[], contentHeight: number): Comment[] {
  if (contentHeight <= 0) return comments;
  let migrated = false;
  const result = comments.map((c) => {
    if (c.y > 100) {
      migrated = true;
      return { ...c, y: Math.min(100, Math.max(0, (c.y / contentHeight) * 100)) };
    }
    return c;
  });
  if (migrated) {
    // Migration from pixel to percentage positioning completed silently
  }
  return result;
}

/**
 * Extract @mentions from text. Returns array of unique usernames (lowercased).
 * Pattern: @username (alphanumeric + underscores, 2-30 chars)
 */
function extractMentions(text: string): string[] {
  const regex = /@([a-zA-Z0-9_\u0590-\u05FF]{2,30})\b/g;
  const mentions: string[] = [];
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    const username = match[1].toLowerCase();
    if (!mentions.includes(username)) {
      mentions.push(username);
    }
  }
  return mentions;
}

/**
 * Render comment text with highlighted @mentions.
 */
function renderTextWithMentions(text: string): React.ReactNode {
  const regex = /@([a-zA-Z0-9_\u0590-\u05FF]{2,30})\b/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = regex.exec(text)) !== null) {
    // Text before the mention
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    // The mention itself — highlighted
    parts.push(
      <span
        key={`mention-${key++}`}
        className="inline-block bg-purple-100 text-purple-700 rounded px-1 text-xs"
        style={{ fontWeight: 600, direction: "ltr", unicodeBidi: "embed" }}
      >
        @{match[1]}
      </span>
    );
    lastIndex = match.index + match[0].length;
  }
  // Remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length > 0 ? parts : text;
}

export function CommentSystem({ children }: { children: React.ReactNode }) {
  const siteId = useTemplateId();
  const relativePath = useRelativePath();
  const editCtx = useContentEditingSafe();
  const isPreviewMode = editCtx?.isPreviewMode ?? false;
  const [comments, setComments] = useState<Comment[]>([]);
  const [isCommentMode, setIsCommentMode] = useState(false);
  const [activePin, setActivePin] = useState<string | null>(null);
  const [highlightedCommentId, setHighlightedCommentId] = useState<string | null>(null);
  const [newCommentPos, setNewCommentPos] = useState<{ x: number; y: number } | null>(null);
  const [newText, setNewText] = useState("");
  const [author, setAuthor] = useState("");
  const [showPanel, setShowPanel] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notifStatus, setNotifStatus] = useState<{ type: "ok" | "warn" | "error"; text: string } | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Reply state
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState("");
  const [isSavingReply, setIsSavingReply] = useState(false);
  const replyInputRef = useRef<HTMLTextAreaElement>(null);
  // Reply mention state
  const [replyMentionDropdown, setReplyMentionDropdown] = useState(false);
  const [replyMentionQuery, setReplyMentionQuery] = useState("");
  const [replyMentionStart, setReplyMentionStart] = useState(-1);
  const [replyMentionIdx, setReplyMentionIdx] = useState(0);

  // Auto-focus reply textarea when it opens
  useEffect(() => {
    if (replyingTo && replyInputRef.current) {
      setTimeout(() => replyInputRef.current?.focus(), 50);
    }
  }, [replyingTo]);

  // Drag state
  const dragRef = useRef<DragState | null>(null);
  const [dragVisual, setDragVisual] = useState<{ id: string; x: number; y: number } | null>(null);
  const justFinishedDragRef = useRef(false);

  // Panel filter state: "all" | "open" | "resolved"
  const [panelFilter, setPanelFilter] = useState<"all" | "open" | "resolved">("all");

  // ======== Mention state ========
  const [allUsers, setAllUsers] = useState<UserRecord[]>([]);
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionStartIndex, setMentionStartIndex] = useState(-1);
  const [mentionSelectedIdx, setMentionSelectedIdx] = useState(0);
  const mentionDropdownRef = useRef<HTMLDivElement>(null);

  // Reply mention suggestions — must be after allUsers is declared
  const replyMentionSuggestions = allUsers.filter((u) => {
    if (!replyMentionQuery) return true;
    const q = replyMentionQuery.toLowerCase();
    return u.username.toLowerCase().includes(q) || (u.displayName && u.displayName.toLowerCase().includes(q));
  }).slice(0, 6);

  // Load users for mention suggestions
  useEffect(() => {
    listUsers()
      .then((users) => setAllUsers(users))
      .catch((err) => console.error("[CommentSystem] Failed to load users for mentions:", err));
  }, []);

  // Filtered mention suggestions
  const mentionSuggestions = allUsers.filter((u) => {
    if (!mentionQuery) return true;
    const q = mentionQuery.toLowerCase();
    return (
      u.username.toLowerCase().includes(q) ||
      (u.displayName && u.displayName.toLowerCase().includes(q))
    );
  }).slice(0, 8);

  // Load comments from Supabase — filtered by siteId
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setIsLoading(true);
      setError(null);
      try {
        const data = await apiFetch("/comments");
        if (!cancelled) {
          let loaded = filterBySite((data.comments || []) as Comment[], siteId);
          if (contentRef.current) {
            loaded = migrateOldComments(loaded, contentRef.current.scrollHeight);
          }
          setComments(loaded);
        }
      } catch (err) {
        console.error("[CommentSystem] Failed to load comments:", err);
        if (!cancelled) {
          setError("\u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05D8\u05E2\u05D9\u05E5\u05EA \u05D4\u05E2\u05E8\u05D5\u05EA \u05DE\u05D4\u05E9\u05E8\u05EA");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  // Poll for new comments every 15 seconds — smart merge to avoid overwriting
  // in-flight reply saves. Merges server data with local optimistic state.
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const data = await apiFetch("/comments");
        let loaded = filterBySite(data.comments || [], siteId);
        if (contentRef.current) {
          loaded = migrateOldComments(loaded, contentRef.current.scrollHeight);
        }
        // Smart merge: if a local comment has newer replies than server,
        // keep the local version (optimistic save still in-flight)
        setComments((prev) => {
          const prevMap = new Map(prev.map((c) => [c.id, c]));
          return loaded.map((serverC) => {
            const localC = prevMap.get(serverC.id);
            if (!localC) return serverC;
            // If local has more replies, keep local (optimistic add)
            const localReplies = localC.replies?.length || 0;
            const serverReplies = serverC.replies?.length || 0;
            if (localReplies > serverReplies) return localC;
            return serverC;
          });
        });
        setError(null); // clear any previous errors on success
      } catch {
        // silent — don't spam errors on polling
      }
    }, 15000);
    return () => clearInterval(interval);
  }, []);

  // Load author name from localStorage (just a UI preference, not comment data)
  useEffect(() => {
    try {
      const storedAuthor = localStorage.getItem(AUTHOR_KEY);
      if (storedAuthor) setAuthor(storedAuthor);
    } catch { /* ignore */ }
  }, []);

  // Check for pending comment highlight (from notification click)
  useEffect(() => {
    if (isLoading || comments.length === 0) return;
    try {
      const pendingId = sessionStorage.getItem("zap_highlight_comment");
      if (!pendingId) return;
      sessionStorage.removeItem("zap_highlight_comment");
      const target = comments.find((c) => c.id === pendingId);
      if (target && target.page === relativePath) {
        // Scroll to comment and highlight it
        setTimeout(() => {
          scrollToComment(target);
          setHighlightedCommentId(pendingId);
          setIsCommentMode(true);
          // Clear highlight after animation
          setTimeout(() => setHighlightedCommentId(null), 3000);
        }, 500);
      }
    } catch { /* ignore */ }
  }, [isLoading, comments, relativePath]);

  // Save author name locally
  useEffect(() => {
    if (author) {
      try {
        localStorage.setItem(AUTHOR_KEY, author);
      } catch { /* ignore */ }
    }
  }, [author]);

  // Close active pin when switching pages
  useEffect(() => {
    setActivePin(null);
    setNewCommentPos(null);
  }, [relativePath]);

  const pageComments = comments.filter((c) => c.page === relativePath);
  const allPagesWithComments = [...new Set(comments.map((c) => c.page))];

  // Convert viewport coordinates to content coordinates (percentage-based)
  const viewportToContent = useCallback((clientX: number, clientY: number) => {
    if (!contentRef.current) return null;
    const rect = contentRef.current.getBoundingClientRect();
    const contentHeight = contentRef.current.scrollHeight;

    // X: percentage from right (RTL)
    const xFromRight = rect.right - clientX;
    const xPercent = (xFromRight / rect.width) * 100;

    // Y: percentage of total content height
    const yFromContentTop = clientY - rect.top;
    const yPercent = (yFromContentTop / contentHeight) * 100;

    return {
      x: Math.max(0, Math.min(100, xPercent)),
      y: Math.max(0, Math.min(100, yPercent)),
    };
  }, []);

  // Helper: convert comment percentage Y to absolute px for scrolling
  const getAbsoluteY = useCallback((yPercent: number) => {
    if (!contentRef.current) return 0;
    return (yPercent / 100) * contentRef.current.scrollHeight;
  }, []);

  // ======== Mention handlers ========
  const handleTextareaChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const val = e.target.value;
      setNewText(val);

      // Detect if user is typing a mention
      const textarea = e.target;
      const cursorPos = textarea.selectionStart || 0;
      const textBeforeCursor = val.slice(0, cursorPos);

      // Find the last @ that's either at start of string or preceded by whitespace
      const lastAtIndex = textBeforeCursor.lastIndexOf("@");
      if (lastAtIndex >= 0) {
        const charBefore = lastAtIndex === 0 ? " " : textBeforeCursor[lastAtIndex - 1];
        if (charBefore === " " || charBefore === "\n" || lastAtIndex === 0) {
          const queryAfterAt = textBeforeCursor.slice(lastAtIndex + 1);
          // Only show dropdown if query doesn't contain spaces (single word)
          if (!queryAfterAt.includes(" ") && !queryAfterAt.includes("\n")) {
            setShowMentionDropdown(true);
            setMentionQuery(queryAfterAt);
            setMentionStartIndex(lastAtIndex);
            setMentionSelectedIdx(0);
            return;
          }
        }
      }
      setShowMentionDropdown(false);
      setMentionQuery("");
      setMentionStartIndex(-1);
    },
    []
  );

  const insertMention = useCallback(
    (user: UserRecord) => {
      if (mentionStartIndex < 0 || !inputRef.current) return;
      const textarea = inputRef.current;
      const cursorPos = textarea.selectionStart || 0;
      const before = newText.slice(0, mentionStartIndex);
      const after = newText.slice(cursorPos);
      const mentionText = `@${user.username} `;
      const newVal = before + mentionText + after;
      setNewText(newVal);
      setShowMentionDropdown(false);
      setMentionQuery("");
      setMentionStartIndex(-1);

      // Restore focus and cursor position
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          const newCursor = before.length + mentionText.length;
          inputRef.current.selectionStart = newCursor;
          inputRef.current.selectionEnd = newCursor;
        }
      }, 10);
    },
    [mentionStartIndex, newText]
  );

  const handleTextareaKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // Mention dropdown navigation
      if (showMentionDropdown && mentionSuggestions.length > 0) {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setMentionSelectedIdx((prev) => (prev + 1) % mentionSuggestions.length);
          return;
        }
        if (e.key === "ArrowUp") {
          e.preventDefault();
          setMentionSelectedIdx((prev) => (prev - 1 + mentionSuggestions.length) % mentionSuggestions.length);
          return;
        }
        if (e.key === "Enter" || e.key === "Tab") {
          e.preventDefault();
          insertMention(mentionSuggestions[mentionSelectedIdx]);
          return;
        }
        if (e.key === "Escape") {
          e.preventDefault();
          setShowMentionDropdown(false);
          return;
        }
      }

      // Normal shortcuts
      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
        addComment();
      }
      if (e.key === "Escape") {
        setNewCommentPos(null);
      }
    },
    [showMentionDropdown, mentionSuggestions, mentionSelectedIdx, insertMention]
  );

  // ======== Drag handlers ========
  const handlePinMouseDown = useCallback(
    (e: React.MouseEvent, commentId: string) => {
      if (e.button !== 0) return;
      if ((e.target as HTMLElement).closest("[data-comment-bubble]")) return;

      e.preventDefault();
      e.stopPropagation();

      dragRef.current = {
        commentId,
        startMouseX: e.clientX,
        startMouseY: e.clientY,
        currentX: e.clientX,
        currentY: e.clientY,
        isDragging: false,
      };
    },
    []
  );

  // Global mouse move/up for drag
  useEffect(() => {
    function handleMouseMove(e: MouseEvent) {
      const drag = dragRef.current;
      if (!drag) return;

      const dx = e.clientX - drag.startMouseX;
      const dy = e.clientY - drag.startMouseY;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (!drag.isDragging && distance >= DRAG_THRESHOLD) {
        drag.isDragging = true;
        setActivePin(null);
        setNewCommentPos(null);
      }

      if (drag.isDragging) {
        drag.currentX = e.clientX;
        drag.currentY = e.clientY;
        setDragVisual({ id: drag.commentId, x: e.clientX, y: e.clientY });
      }
    }

    function handleMouseUp(e: MouseEvent) {
      const drag = dragRef.current;
      if (!drag) return;

      if (drag.isDragging) {
        const newPos = viewportToContent(e.clientX, e.clientY);
        if (newPos) {
          updateCommentPosition(drag.commentId, newPos.x, newPos.y);
        }
        setDragVisual(null);
      } else {
        setActivePin((prev) => (prev === drag.commentId ? null : drag.commentId));
        setNewCommentPos(null);
      }

      dragRef.current = null;
      justFinishedDragRef.current = true;
      setTimeout(() => { justFinishedDragRef.current = false; }, 100);
    }

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [viewportToContent]);

  // Update comment position (optimistic + server)
  const updateCommentPosition = useCallback(
    async (id: string, newX: number, newY: number) => {
      const prevComments = comments;
      setComments((prev) =>
        prev.map((c) => (c.id === id ? { ...c, x: newX, y: newY } : c))
      );

      try {
        await apiFetch(`/comments/${id}`, {
          method: "PUT",
          body: JSON.stringify({ x: newX, y: newY }),
        });
      } catch (err) {
        console.error("[CommentSystem] Failed to update comment position:", err);
        setComments(prevComments);
        setError("\u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05E2\u05D3\u05DB\u05D5\u05DF \u05DE\u05D9\u05E7\u05D5\u05DD \u05D4\u05D4\u05E2\u05E8\u05D4");
        setTimeout(() => setError(null), 4000);
      }
    },
    [comments]
  );

  const handleContentClick = useCallback(
    (e: React.MouseEvent) => {
      if (!isCommentMode || !contentRef.current) return;
      if (justFinishedDragRef.current) return;

      const target = e.target as HTMLElement;
      if (target.closest("[data-comment-pin]") || target.closest("[data-comment-panel]") || target.closest("[data-comment-bubble]")) {
        return;
      }

      const pos = viewportToContent(e.clientX, e.clientY);
      if (!pos) return;

      setNewCommentPos(pos);
      setActivePin(null);
      setNewText("");

      setTimeout(() => inputRef.current?.focus(), 100);
    },
    [isCommentMode, viewportToContent]
  );

  const addComment = async () => {
    if (!newText.trim() || !newCommentPos) return;

    // Extract mentions from the text
    const mentions = extractMentions(newText);

    const comment: Comment = {
      id: generateId(),
      x: newCommentPos.x,
      y: newCommentPos.y,
      text: newText.trim(),
      author: author.trim() || "\u05D0\u05E0\u05D5\u05E0\u05D9\u05DE\u05D9",
      timestamp: Date.now(),
      page: relativePath,
      resolved: false,
      siteId, // Include siteId for data isolation
      mentions: mentions.length > 0 ? mentions : undefined,
    };

    console.log("[CommentSystem] Saving comment:", { id: comment.id, siteId: comment.siteId, page: comment.page, textLength: comment.text.length });

    // Optimistic add
    setComments((prev) => [...prev, comment]);
    setNewCommentPos(null);
    setNewText("");
    setShowMentionDropdown(false);
    setIsSaving(true);
    setError(null);

    try {
      const result = await apiFetch("/comments", {
        method: "POST",
        body: JSON.stringify(comment),
      });
      console.log("[CommentSystem] Comment saved successfully:", result?.success);
      // Log notification & mention debug info from server
      if (result.notificationDebug) {
        console.log("[CommentSystem] Notification debug:", JSON.stringify(result.notificationDebug, null, 2));
      }
      if (result.mentionDebug) {
        console.log("[CommentSystem] Mention debug:", JSON.stringify(result.mentionDebug, null, 2));
      }

      // ── Build user-visible notification status ──
      setNotifStatus(null);
      const nd = result.notificationDebug;
      const md = result.mentionDebug;
      if (nd || md) {
        const assignedResults = nd?.results || [];
        const mentionResults = md?.results || [];
        const allResults = [...assignedResults, ...mentionResults];
        const emailsSent = allResults.filter((r: any) => r.emailSent).length;
        const inAppSent = allResults.filter((r: any) => r.inAppSaved).length;
        const emailErrors = allResults.filter((r: any) => r.emailError && !r.emailSent);
        const mentionCount = md?.mentions?.length || 0;

        // Priority: show actual results first, then diagnostics
        if (emailsSent > 0) {
          setNotifStatus({ type: "ok", text: `\u2709 ${emailsSent} \u05DE\u05D9\u05D9\u05DC\u05D9\u05DD + ${inAppSent} \u05D4\u05EA\u05E8\u05D0\u05D5\u05EA in-app \u05E0\u05E9\u05DC\u05D7\u05D5` });
        } else if (inAppSent > 0 && emailErrors.length > 0) {
          const firstErr = emailErrors[0];
          const reason = firstErr
            ? (firstErr.emailError?.includes("no email") || firstErr.emailError?.includes("User has no email")
              ? `\u05DC-${firstErr.username} \u05D0\u05D9\u05DF \u05DB\u05EA\u05D5\u05D1\u05EA \u05DE\u05D9\u05D9\u05DC`
              : firstErr.emailError?.includes("User not found")
              ? `\u05DE\u05E9\u05EA\u05DE\u05E9 ${firstErr.username} \u05DC\u05D0 \u05E0\u05DE\u05E6\u05D0 \u05D1\u05DE\u05E2\u05E8\u05DB\u05EA`
              : `${firstErr.username}: ${firstErr.emailError || "\u05E9\u05D2\u05D9\u05D0\u05D4"}`)
            : "\u05D0\u05D9\u05DF \u05DB\u05EA\u05D5\u05D1\u05EA \u05DE\u05D9\u05D9\u05DC";
          setNotifStatus({ type: "warn", text: `${inAppSent} \u05D4\u05EA\u05E8\u05D0\u05D5\u05EA in-app \u05E0\u05E9\u05DC\u05D7\u05D5 | \u05DE\u05D9\u05D9\u05DC \u05DC\u05D0 \u05E0\u05E9\u05DC\u05D7: ${reason}` });
        } else if (inAppSent > 0) {
          setNotifStatus({ type: "ok", text: `${inAppSent} \u05D4\u05EA\u05E8\u05D0\u05D5\u05EA in-app \u05E0\u05E9\u05DC\u05D7\u05D5 (\u05DC\u05DC\u05D0 \u05DE\u05D9\u05D9\u05DC)` });
        } else if (allResults.length === 0) {
          // No results at all — explain why
          const parts: string[] = [];
          if (!nd?.siteId) parts.push("siteId \u05D7\u05E1\u05E8");
          else if (!nd?.templateFound) parts.push("\u05EA\u05D1\u05E0\u05D9\u05EA \u05DC\u05D0 \u05E0\u05DE\u05E6\u05D0\u05D4 \u05D1-KV");
          else if ((nd?.assignedUsers?.length || 0) === 0) parts.push("\u05D0\u05D9\u05DF \u05DE\u05E9\u05EA\u05DE\u05E9\u05D9\u05DD \u05DE\u05D5\u05E7\u05E6\u05D9\u05DD");
          if (mentionCount === 0) parts.push("\u05D0\u05D9\u05DF \u05EA\u05D9\u05D5\u05D2\u05D9\u05DD (@)");
          setNotifStatus({ type: "warn", text: `\u05D4\u05EA\u05E8\u05D0\u05D5\u05EA \u05DC\u05D0 \u05E0\u05E9\u05DC\u05D7\u05D5: ${parts.join(" | ") || "\u05DC\u05D0 \u05D9\u05D3\u05D5\u05E2"}` });
        } else {
          setNotifStatus({ type: "error", text: `\u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05E9\u05DC\u05D9\u05D7\u05EA \u05D4\u05EA\u05E8\u05D0\u05D5\u05EA: ${emailErrors[0]?.emailError || "\u05DC\u05D0 \u05D9\u05D3\u05D5\u05E2"}` });
        }
        // Auto-dismiss after 12 seconds
        setTimeout(() => setNotifStatus(null), 12000);
      }
    } catch (err: any) {
      console.error("[CommentSystem] Failed to save comment:", err?.message || err);
      // Revert the optimistic add
      setComments((prev) => prev.filter((c) => c.id !== comment.id));
      const is401 = err?.message?.includes("401");
      const errorMsg = is401
        ? "\u05E9\u05D2\u05D9\u05D0\u05EA \u05D0\u05D9\u05DE\u05D5\u05EA \u2014 \u05E0\u05E1\u05D5 \u05DC\u05D4\u05EA\u05D7\u05D1\u05E8 \u05DE\u05D7\u05D3\u05E9"
        : `\u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05E9\u05DE\u05D9\u05E8\u05EA \u05D4\u05D4\u05E2\u05E8\u05D4: ${err?.message || "\u05E0\u05E1\u05D5 \u05E9\u05D5\u05D1"}`;
      setError(errorMsg);
      setTimeout(() => setError(null), 8000);
    } finally {
      setIsSaving(false);
    }
  };

  const deleteComment = async (id: string) => {
    const prev = comments;
    setComments((c) => c.filter((item) => item.id !== id));
    setActivePin(null);

    try {
      await apiFetch(`/comments/${id}`, { method: "DELETE" });
    } catch (err: any) {
      console.error("[CommentSystem] Failed to delete comment:", err?.message || err);
      setComments(prev);
      setError(`\u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05DE\u05D7\u05D9\u05E7\u05EA \u05D4\u05D4\u05E2\u05E8\u05D4: ${err?.message || "\u05E0\u05E1\u05D5 \u05E9\u05D5\u05D1"}`);
      setTimeout(() => setError(null), 8000);
    }
  };

  const toggleResolve = async (id: string) => {
    const target = comments.find((c) => c.id === id);
    if (!target) return;
    const newResolved = !target.resolved;

    setComments((prev) => prev.map((c) => (c.id === id ? { ...c, resolved: newResolved } : c)));

    try {
      await apiFetch(`/comments/${id}`, {
        method: "PUT",
        body: JSON.stringify({ resolved: newResolved }),
      });
    } catch (err) {
      console.error("[CommentSystem] Failed to toggle resolve:", err);
      setComments((prev) => prev.map((c) => (c.id === id ? { ...c, resolved: !newResolved } : c)));
      setError("\u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05E2\u05D3\u05DB\u05D5\u05DF \u05D4\u05D4\u05E2\u05E8\u05D4");
      setTimeout(() => setError(null), 4000);
    }
  };

  const scrollToComment = (comment: Comment) => {
    const yPx = getAbsoluteY(comment.y);
    window.scrollTo({ top: Math.max(0, yPx - 200), behavior: "smooth" });
    setActivePin(comment.id);
  };

  // ======== Reply handler ========
  const addReply = async (commentId: string) => {
    if (!replyText.trim()) return;
    const reply: CommentReply = {
      id: generateId(),
      text: replyText.trim(),
      author: author.trim() || "\u05D0\u05E0\u05D5\u05E0\u05D9\u05DE\u05D9",
      timestamp: Date.now(),
    };
    // Optimistic update
    setComments((prev) =>
      prev.map((c) =>
        c.id === commentId
          ? { ...c, replies: [...(c.replies || []), reply] }
          : c
      )
    );
    setReplyText("");
    setReplyingTo(null);
    setIsSavingReply(true);
    try {
      const target = comments.find((c) => c.id === commentId);
      const updatedReplies = [...(target?.replies || []), reply];
      await apiFetch(`/comments/${commentId}`, {
        method: "PUT",
        body: JSON.stringify({ replies: updatedReplies }),
      });
    } catch (err) {
      console.error("[CommentSystem] Failed to save reply:", err);
      // Revert
      setComments((prev) =>
        prev.map((c) =>
          c.id === commentId
            ? { ...c, replies: (c.replies || []).filter((r) => r.id !== reply.id) }
            : c
        )
      );
      setError("\u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05E9\u05DE\u05D9\u05E8\u05EA \u05EA\u05D2\u05D5\u05D1\u05D4");
      setTimeout(() => setError(null), 4000);
    } finally {
      setIsSavingReply(false);
    }
  };

  // ======== Reply mention handlers ========
  const handleReplyTextChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setReplyText(val);
    const cursorPos = e.target.selectionStart || 0;
    const textBefore = val.slice(0, cursorPos);
    const lastAt = textBefore.lastIndexOf("@");
    if (lastAt >= 0) {
      const charBefore = lastAt === 0 ? " " : textBefore[lastAt - 1];
      if (charBefore === " " || charBefore === "\n" || lastAt === 0) {
        const queryAfter = textBefore.slice(lastAt + 1);
        if (!queryAfter.includes(" ") && !queryAfter.includes("\n")) {
          setReplyMentionDropdown(true);
          setReplyMentionQuery(queryAfter);
          setReplyMentionStart(lastAt);
          setReplyMentionIdx(0);
          return;
        }
      }
    }
    setReplyMentionDropdown(false);
    setReplyMentionQuery("");
    setReplyMentionStart(-1);
  }, []);

  const insertReplyMention = useCallback((user: UserRecord) => {
    if (replyMentionStart < 0 || !replyInputRef.current) return;
    const textarea = replyInputRef.current;
    const cursorPos = textarea.selectionStart || 0;
    const before = replyText.slice(0, replyMentionStart);
    const after = replyText.slice(cursorPos);
    const mentionText = `@${user.username} `;
    setReplyText(before + mentionText + after);
    setReplyMentionDropdown(false);
    setReplyMentionQuery("");
    setReplyMentionStart(-1);
    setTimeout(() => {
      if (replyInputRef.current) {
        replyInputRef.current.focus();
        const newCursor = before.length + mentionText.length;
        replyInputRef.current.selectionStart = newCursor;
        replyInputRef.current.selectionEnd = newCursor;
      }
    }, 10);
  }, [replyMentionStart, replyText]);

  // ======== Delete single reply handler ========
  const deleteReply = async (commentId: string, replyId: string) => {
    const target = comments.find((c) => c.id === commentId);
    if (!target) return;
    const prevReplies = target.replies || [];
    // Optimistic remove
    setComments((prev) =>
      prev.map((c) =>
        c.id === commentId
          ? { ...c, replies: (c.replies || []).filter((r) => r.id !== replyId) }
          : c
      )
    );
    try {
      const updatedReplies = prevReplies.filter((r) => r.id !== replyId);
      await apiFetch(`/comments/${commentId}`, {
        method: "PUT",
        body: JSON.stringify({ replies: updatedReplies }),
      });
    } catch (err) {
      console.error("[CommentSystem] Failed to delete reply:", err);
      // Revert
      setComments((prev) =>
        prev.map((c) => (c.id === commentId ? { ...c, replies: prevReplies } : c))
      );
      setError("\u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05DE\u05D7\u05D9\u05E7\u05EA \u05EA\u05D2\u05D5\u05D1\u05D4");
      setTimeout(() => setError(null), 4000);
    }
  };

  // ======== Role label map for display ========
  const roleLabels: Record<string, string> = {
    admin: "\u05DE\u05E0\u05D4\u05DC",
    characterizer: "\u05D0\u05E4\u05D9\u05D9\u05E0\u05D9\u05E1\u05D8",
    designer: "\u05DE\u05E2\u05E6\u05D1",
    content_writer: "\u05DB\u05D5\u05EA\u05D1 \u05EA\u05D5\u05DB\u05DF",
    developer: "\u05DE\u05E4\u05EA\u05D7",
    marketer: "\u05DE\u05E9\u05D5\u05D5\u05E7",
  };

  return (
    <div className="relative">
      {/* Content wrapper */}
      <div
        ref={contentRef}
        onClick={handleContentClick}
        className={`${isCommentMode ? "cursor-crosshair" : ""}`}
        style={{ position: "relative" }}
      >
        {children}

        {/* Render comment pins for current page */}
        {!isPreviewMode && pageComments.map((comment, index) => {
          const isBeingDragged = dragVisual?.id === comment.id;

          return (
            <div
              key={comment.id}
              data-comment-pin
              className={`absolute z-[998] group ${isBeingDragged ? "pointer-events-none opacity-30" : ""}`}
              style={{
                right: `${comment.x}%`,
                top: `${comment.y}%`,
                transform: "translate(50%, -50%)",
              }}
            >
              {/* Pin marker */}
              <div
                onMouseDown={(e) => handlePinMouseDown(e, comment.id)}
                className={`relative w-8 h-8 rounded-full flex items-center justify-center text-white text-xs shadow-lg select-none ${
                  comment.resolved
                    ? "bg-green-500 hover:bg-green-600"
                    : comment.mentions && comment.mentions.length > 0
                    ? "bg-purple-500 hover:bg-purple-600"
                    : "bg-zap hover:bg-zap-dark"
                } ${activePin === comment.id ? "ring-2 ring-white ring-offset-2 scale-110" : ""} ${
                  highlightedCommentId === comment.id ? "ring-4 ring-yellow-400 ring-offset-2 scale-125 animate-pulse" : ""
                } ${
                  isCommentMode ? "cursor-grab active:cursor-grabbing hover:scale-110" : "cursor-pointer hover:scale-110"
                }`}
                style={{ fontWeight: 700 }}
              >
                {comment.mentions && comment.mentions.length > 0 && !comment.resolved ? (
                  <AtSign className="w-4 h-4" />
                ) : (
                  index + 1
                )}
                {/* Reply count badge */}
                {comment.replies && comment.replies.length > 0 && (
                  <div className="absolute -top-1 -left-1 w-4 h-4 rounded-full bg-white text-[9px] text-zap-navy flex items-center justify-center shadow-sm border border-gray-200" style={{ fontWeight: 700 }}>
                    {comment.replies.length}
                  </div>
                )}
                {/* Drag grip hint — visible on hover in comment mode */}
                {isCommentMode && (
                  <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 pointer-events-none">
                    <span className="text-[9px] bg-gray-800/80 text-white px-1.5 py-0.5 rounded whitespace-nowrap">
                      {"\u05D2\u05E8\u05D5\u05E4\u05DC"}
                    </span>
                  </div>
                )}
              </div>

              {/* Comment bubble */}
              {activePin === comment.id && !isBeingDragged && (
                <div
                  data-comment-bubble
                  className="absolute top-10 left-1/2 -translate-x-1/2 w-72 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden z-[999]"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="p-3 border-b border-gray-100 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-zap text-white flex items-center justify-center text-xs" style={{ fontWeight: 700 }}>
                        {comment.author.charAt(0)}
                      </div>
                      <div>
                        <p className="text-xs text-zap-navy" style={{ fontWeight: 600 }}>{comment.author}</p>
                        <p className="text-[10px] text-gray-400">{formatTime(comment.timestamp)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => toggleResolve(comment.id)}
                        className={`btnReset p-1 rounded text-xs ${
                          comment.resolved ? "text-green-600 bg-green-50" : "text-gray-400 hover:text-green-600 hover:bg-green-50"
                        }`}
                        title={comment.resolved ? "\u05E1\u05D5\u05DE\u05DF \u05DB\u05D8\u05D5\u05E4\u05DC" : "\u05E1\u05DE\u05DF \u05DB\u05D8\u05D5\u05E4\u05DC"}
                      >
                        {"\u2713"}
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteComment(comment.id)}
                        className="btnReset p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50"
                        title={"\u05DE\u05D7\u05E7 \u05D4\u05E2\u05E8\u05D4"}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setActivePin(null)}
                        className="btnReset p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  <div className="p-3">
                    <div className={`text-sm text-gray-700 leading-relaxed whitespace-pre-wrap ${comment.resolved ? "line-through text-gray-400" : ""}`}>
                      {renderTextWithMentions(comment.text)}
                    </div>
                    {/* Show mentioned users */}
                    {comment.mentions && comment.mentions.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-gray-100 flex flex-wrap gap-1">
                        <AtSign className="w-3 h-3 text-purple-400 mt-0.5" />
                        {comment.mentions.map((m) => {
                          const user = allUsers.find((u) => u.username.toLowerCase() === m.toLowerCase());
                          return (
                            <span key={m} className="text-[10px] bg-purple-50 text-purple-600 rounded px-1.5 py-0.5" style={{ fontWeight: 500 }}>
                              {user?.displayName || m}
                            </span>
                          );
                        })}
                      </div>
                    )}
                    {/* Show replies */}
                    {comment.replies && comment.replies.length > 0 && (
                      <div className="mt-2">
                        {comment.replies.map((reply) => (
                          <div key={reply.id} className="flex items-start gap-2 mt-1.5 pt-1.5 border-t border-gray-50">
                            <div
                              className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-white text-[10px] bg-gray-400"
                              style={{ fontWeight: 700 }}
                            >
                              {reply.author.charAt(0)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <p className="text-xs text-zap-navy truncate" style={{ fontWeight: 600 }}>{reply.author}</p>
                                <div className="flex items-center gap-1">
                                  <span className="text-[10px] text-gray-400">{formatTime(reply.timestamp)}</span>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      deleteReply(comment.id, reply.id);
                                    }}
                                    className="btnReset p-0.5 text-gray-300 hover:text-red-500"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                              </div>
                              <div className={`text-xs text-gray-600 mt-1 line-clamp-2 ${comment.resolved ? "line-through text-gray-400" : ""}`}>
                                {renderTextWithMentions(reply.text)}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Reply input */}
                    {replyingTo === comment.id && (
                      <div className="mt-2 relative">
                        <textarea
                          ref={replyInputRef}
                          value={replyText}
                          onChange={handleReplyTextChange}
                          placeholder={"\u05DB\u05EA\u05D5\u05D1 \u05EA\u05D2\u05D5\u05D1\u05D4... (\u05D4\u05E7\u05DC\u05D3 @ \u05DC\u05EA\u05D9\u05D5\u05D2)"}
                          className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-zap resize-none text-right"
                          rows={3}
                          onKeyDown={(e) => {
                            if (replyMentionDropdown && replyMentionSuggestions.length > 0) {
                              if (e.key === "ArrowDown") { e.preventDefault(); setReplyMentionIdx((p) => (p + 1) % replyMentionSuggestions.length); return; }
                              if (e.key === "ArrowUp") { e.preventDefault(); setReplyMentionIdx((p) => (p - 1 + replyMentionSuggestions.length) % replyMentionSuggestions.length); return; }
                              if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); insertReplyMention(replyMentionSuggestions[replyMentionIdx]); return; }
                              if (e.key === "Escape") { e.preventDefault(); setReplyMentionDropdown(false); return; }
                            }
                            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                              addReply(comment.id);
                            }
                            if (e.key === "Escape") {
                              setReplyText("");
                              setReplyingTo(null);
                            }
                          }}
                        />
                        {/* Reply mention dropdown */}
                        {replyMentionDropdown && replyMentionSuggestions.length > 0 && (
                          <div className="absolute bottom-full left-0 right-0 mb-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-32 overflow-y-auto">
                            {replyMentionSuggestions.map((u, i) => (
                              <button
                                key={u.username}
                                type="button"
                                onClick={() => insertReplyMention(u)}
                                className={`btnReset w-full flex items-center gap-2 px-3 py-1.5 text-right text-xs hover:bg-indigo-50 ${i === replyMentionIdx ? "bg-indigo-50" : ""}`}
                              >
                                <span className="w-5 h-5 rounded-full bg-zap text-white flex items-center justify-center text-[9px]" style={{ fontWeight: 700 }}>{u.username.charAt(0).toUpperCase()}</span>
                                <span style={{ fontWeight: 600 }}>{u.displayName || u.username}</span>
                                <span className="text-gray-400">@{u.username}</span>
                              </button>
                            ))}
                          </div>
                        )}
                        <div className="flex items-center justify-between mt-2">
                          <p className="text-[10px] text-gray-400">{"Ctrl+Enter \u05DC\u05E9\u05DC\u05D9\u05D7\u05D4 | Esc \u05DC\u05D1\u05D9\u05D8\u05D5\u05DC | @ \u05DC\u05EA\u05D9\u05D5\u05D2"}</p>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => setReplyingTo(null)}
                              className="btnReset px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-100 rounded-lg"
                            >
                              {"\u05D1\u05D9\u05D8\u05D5\u05DC"}
                            </button>
                            <button
                              type="button"
                              onClick={() => addReply(comment.id)}
                              disabled={!replyText.trim() || isSavingReply}
                              className="btnReset px-3 py-1.5 text-xs bg-zap text-white rounded-lg hover:bg-zap-dark disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
                            >
                              {isSavingReply ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                              {"\u05E9\u05DC\u05D7"}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    {/* Reply button */}
                    {!replyingTo && (
                      <div className="mt-2">
                        <button
                          type="button"
                          onClick={() => setReplyingTo(comment.id)}
                          className="btnReset flex items-center gap-1 px-3 py-1.5 text-xs bg-zap text-white rounded-lg hover:bg-zap-dark"
                        >
                          <Reply className="w-3 h-3" />
                          {"\u05D4\u05D2\u05D1"}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* Drag ghost — follows cursor in viewport */}
        {dragVisual && (
          <div
            className="fixed z-[9999] pointer-events-none"
            style={{
              left: `${dragVisual.x}px`,
              top: `${dragVisual.y}px`,
              transform: "translate(-50%, -50%)",
            }}
          >
            <div className="w-10 h-10 rounded-full bg-zap text-white flex items-center justify-center text-sm shadow-2xl ring-4 ring-zap/30 animate-pulse"
              style={{ fontWeight: 700 }}
            >
              <GripVertical className="w-4 h-4" />
            </div>
            <div className="text-center mt-1">
              <span className="text-[10px] bg-gray-800/80 text-white px-2 py-0.5 rounded">
                {"\u05E9\u05D7\u05E8\u05E8 \u05DB\u05D3\u05D9 \u05DC\u05DE\u05E7\u05DD"}
              </span>
            </div>
          </div>
        )}

        {/* New comment input popup */}
        {newCommentPos && (
          <div
            data-comment-bubble
            className="absolute z-[999]"
            style={{
              right: `${newCommentPos.x}%`,
              top: `${newCommentPos.y}%`,
              transform: "translate(50%, 8px)",
            }}
          >
            {/* The pin preview */}
            <div className="w-8 h-8 rounded-full bg-zap flex items-center justify-center text-white shadow-lg mx-auto mb-2 animate-bounce">
              <Pin className="w-4 h-4" />
            </div>

            <div
              className="w-80 bg-white rounded-xl shadow-2xl border border-gray-200"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-3 border-b border-gray-100 rounded-t-xl">
                <input
                  type="text"
                  value={author}
                  onChange={(e) => setAuthor(e.target.value)}
                  placeholder={"\u05D4\u05E9\u05DD \u05E9\u05DC\u05DA..."}
                  className="w-full text-xs border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:border-zap text-right"
                />
              </div>

              {/* Mention dropdown — rendered between header and textarea, no overflow clip */}
              {showMentionDropdown && mentionSuggestions.length > 0 && (
                <div
                  ref={mentionDropdownRef}
                  className="mx-3 mt-2 bg-white rounded-lg shadow-xl border border-purple-200 max-h-48 overflow-y-auto z-[1001]"
                  dir="rtl"
                >
                  <div className="p-1.5 border-b border-gray-100">
                    <p className="text-[10px] text-gray-400 px-2">{"\u05EA\u05D9\u05D9\u05D2 \u05DE\u05E9\u05EA\u05DE\u05E9"}</p>
                  </div>
                  {mentionSuggestions.map((user, idx) => (
                    <button
                      key={user.username}
                      type="button"
                      className={`btnReset w-full flex items-center gap-2 px-3 py-2 text-right hover:bg-purple-50 ${
                        idx === mentionSelectedIdx ? "bg-purple-50" : ""
                      }`}
                      onMouseDown={(e) => {
                        e.preventDefault(); // prevent textarea blur
                        insertMention(user);
                      }}
                      onMouseEnter={() => setMentionSelectedIdx(idx)}
                    >
                      <div className="w-6 h-6 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-[10px] flex-shrink-0" style={{ fontWeight: 700 }}>
                        {user.displayName ? user.displayName.charAt(0) : user.username.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-800 truncate" style={{ fontWeight: 600 }}>
                          {user.displayName || user.username}
                        </p>
                        <p className="text-[10px] text-gray-400 truncate">
                          @{user.username} {"\u00B7"} {roleLabels[user.role] || user.role}
                          {user.email ? ` \u00B7 ${user.email}` : ""}
                        </p>
                      </div>
                      {user.email && (
                        <span className="text-[9px] bg-green-100 text-green-600 rounded px-1 py-0.5 flex-shrink-0">{"\u05DE\u05D9\u05D9\u05DC"}</span>
                      )}
                    </button>
                  ))}
                </div>
              )}

              <div className="p-3">
                <textarea
                  ref={inputRef}
                  value={newText}
                  onChange={handleTextareaChange}
                  placeholder={"\u05DB\u05EA\u05D5\u05D1 \u05D4\u05E2\u05E8\u05D4... (\u05D4\u05E7\u05DC\u05D3 @ \u05DC\u05EA\u05D9\u05D5\u05D2)"}
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-zap resize-none text-right"
                  rows={3}
                  onKeyDown={handleTextareaKeyDown}
                />

                {/* Extracted mentions preview */}
                {(() => {
                  const currentMentions = extractMentions(newText);
                  if (currentMentions.length === 0) return null;
                  return (
                    <div className="flex flex-wrap gap-1 mt-2" dir="rtl">
                      <AtSign className="w-3 h-3 text-purple-400 mt-0.5" />
                      {currentMentions.map((m) => {
                        const user = allUsers.find((u) => u.username.toLowerCase() === m);
                        return (
                          <span key={m} className="text-[10px] bg-purple-50 text-purple-600 rounded px-1.5 py-0.5" style={{ fontWeight: 500 }}>
                            {user?.displayName || m}
                            {user?.email ? " \u2709" : ""}
                          </span>
                        );
                      })}
                    </div>
                  );
                })()}

                <div className="flex items-center justify-between mt-2">
                  <p className="text-[10px] text-gray-400">{"Ctrl+Enter \u05DC\u05E9\u05DC\u05D9\u05D7\u05D4 | Esc \u05DC\u05D1\u05D9\u05D8\u05D5\u05DC | @ \u05DC\u05EA\u05D9\u05D5\u05D2"}</p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setNewCommentPos(null)}
                      className="btnReset px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-100 rounded-lg"
                    >
                      {"\u05D1\u05D9\u05D8\u05D5\u05DC"}
                    </button>
                    <button
                      type="button"
                      onClick={addComment}
                      disabled={!newText.trim() || isSaving}
                      className="btnReset px-3 py-1.5 text-xs bg-zap text-white rounded-lg hover:bg-zap-dark disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
                    >
                      {isSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                      {"\u05E9\u05DC\u05D7"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Floating toggle button — hidden on QA checklist page */}
      {!isPreviewMode && relativePath !== "/qa-checklist" && (
      <button
        type="button"
        data-tour-step="comment-toggle"
        onClick={() => {
          setIsCommentMode(!isCommentMode);
          if (isCommentMode) {
            setNewCommentPos(null);
            setActivePin(null);
          }
        }}
        className={`btnReset fixed bottom-6 left-6 z-[1000] w-14 h-14 rounded-full shadow-xl flex items-center justify-center hover:scale-105 ${
          isCommentMode
            ? "bg-zap-navy text-white ring-4 ring-zap/30"
            : "bg-zap text-white hover:bg-zap-dark"
        }`}
        title={isCommentMode ? "\u05E6\u05D0 \u05DE\u05DE\u05E6\u05D1 \u05D4\u05E2\u05E8\u05D5\u05EA" : "\u05D4\u05D5\u05E1\u05E3 \u05D4\u05E2\u05E8\u05D5\u05EA"}
      >
        {isLoading ? (
          <Loader2 className="w-6 h-6 animate-spin" />
        ) : isCommentMode ? (
          <X className="w-6 h-6" />
        ) : (
          <MessageCircle className="w-6 h-6" />
        )}
        {comments.length > 0 && !isCommentMode && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center" style={{ fontWeight: 700 }}>
            {comments.length}
          </span>
        )}
      </button>
      )}

      {/* Error indicator — shows when API call failed */}
      {!isPreviewMode && error && (
        <div className="fixed bottom-[5.5rem] right-6 z-[1000] flex items-center gap-2" dir="rtl">
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500 text-white rounded-lg shadow-lg text-xs">
            <AlertTriangle className="w-3.5 h-3.5" />
            <span>{error}</span>
            <button type="button" onClick={() => setError(null)} className="btnReset mr-1 hover:bg-red-600 rounded p-0.5">
              <X className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}

      {/* Notification status indicator — shows email/in-app status after comment save */}
      {!isPreviewMode && notifStatus && (
        <div className={`fixed ${error ? "bottom-[7.5rem]" : "bottom-[5.5rem]"} right-6 z-[1000] flex items-center gap-2`} dir="rtl">
          <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg shadow-lg text-xs max-w-sm ${
            notifStatus.type === "ok" ? "bg-green-600 text-white" :
            notifStatus.type === "warn" ? "bg-amber-500 text-white" :
            "bg-red-500 text-white"
          }`}>
            {notifStatus.type === "ok" ? <Mail className="w-3.5 h-3.5 flex-shrink-0" /> :
             <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />}
            <span className="leading-snug">{notifStatus.text}</span>
            <button type="button" onClick={() => setNotifStatus(null)} className="btnReset mr-1 rounded p-0.5 hover:opacity-70 flex-shrink-0">
              <X className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}

      {/* Comment mode indicator bar */}
      {!isPreviewMode && isCommentMode && (
        <div className="fixed top-0 left-0 right-0 z-[1000] bg-zap-navy text-white py-2 px-4 flex items-center justify-between text-sm shadow-lg">
          <div className="flex items-center gap-3">
            <Pin className="w-4 h-4 animate-pulse" />
            <span style={{ fontWeight: 500 }}>{"\u05DE\u05E6\u05D1 \u05D4\u05E2\u05E8\u05D5\u05EA \u2014 \u05DC\u05D7\u05E6\u05D5 \u05DC\u05D4\u05D5\u05E1\u05D9\u05E3 \u05D4\u05E2\u05E8\u05D4, \u05D2\u05E8\u05E8\u05D5 \u05E4\u05D9\u05DF \u05E7\u05D9\u05D9\u05DD \u05DB\u05D3\u05D9 \u05DC\u05D4\u05D6\u05D9\u05D6 \u05D0\u05D5\u05EA\u05D5 | \u05D4\u05E7\u05DC\u05D3 @ \u05DC\u05EA\u05D9\u05D5\u05D2"}</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setShowPanel(!showPanel)}
              className="btnReset flex items-center gap-1 px-3 py-1 rounded-lg bg-white/10 hover:bg-white/20 text-xs"
            >
              <span>{comments.length} {"\u05D4\u05E2\u05E8\u05D5\u05EA"}</span>
              {showPanel ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
            <button
              type="button"
              onClick={() => {
                setIsCommentMode(false);
                setNewCommentPos(null);
                setActivePin(null);
              }}
              className="btnReset px-3 py-1 rounded-lg bg-white/10 hover:bg-white/20 text-xs"
            >
              {"\u05E1\u05D2\u05D5\u05E8 \u2715"}
            </button>
          </div>
        </div>
      )}

      {/* Comments panel */}
      {!isPreviewMode && isCommentMode && showPanel && (
        <div
          data-comment-panel
          className="fixed top-11 left-4 z-[1000] w-80 max-h-[70vh] bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col"
          dir="rtl"
        >
          <div className="p-3 border-b border-gray-100 flex items-center justify-between bg-gray-50">
            <h3 className="text-sm text-zap-navy" style={{ fontWeight: 600 }}>{"\u05DB\u05DC \u05D4\u05D4\u05E2\u05E8\u05D5\u05EA"}</h3>
            <button type="button" onClick={() => setShowPanel(false)} className="btnReset text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="flex items-center gap-0.5 px-3 py-2 border-b border-gray-100 bg-gray-50/50">
            {([
              { key: "all" as const, label: "\u05D4\u05DB\u05DC", count: comments.length },
              { key: "open" as const, label: "\u05E4\u05EA\u05D5\u05D7\u05D5\u05EA", count: comments.filter(c => !c.resolved).length },
              { key: "resolved" as const, label: "\u05D8\u05D5\u05E4\u05DC\u05D5", count: comments.filter(c => c.resolved).length },
            ]).map(tab => (
              <span
                key={tab.key}
                role="button"
                tabIndex={0}
                onClick={() => setPanelFilter(tab.key)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setPanelFilter(tab.key); }}
                className={`px-2 py-1 rounded-md text-[10px] cursor-pointer ${
                  panelFilter === tab.key
                    ? "bg-zap text-white"
                    : "text-gray-500 hover:bg-gray-100"
                }`}
                style={{ fontWeight: panelFilter === tab.key ? 600 : 400 }}
              >
                {tab.label} ({tab.count})
              </span>
            ))}
          </div>

          <div className="overflow-y-auto flex-1">
            {isLoading ? (
              <div className="p-6 text-center text-gray-400 text-sm">
                <Loader2 className="w-10 h-10 mx-auto mb-2 animate-spin opacity-30" />
                <p>{"\u05D8\u05D5\u05E2\u05DF \u05D4\u05E2\u05E8\u05D5\u05EA..."}</p>
              </div>
            ) : allPagesWithComments.length === 0 ? (
              <div className="p-6 text-center text-gray-400 text-sm">
                <MessageCircle className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p>{"\u05D0\u05D9\u05DF \u05D4\u05E2\u05E8\u05D5\u05EA \u05E2\u05D3\u05D9\u05D9\u05DF"}</p>
                <p className="text-xs mt-1">{"\u05DC\u05D7\u05E6\u05D5 \u05D1\u05DB\u05DC \u05DE\u05E7\u05D5\u05DD \u05E2\u05D3\u05D9\u05D9\u05DF \u05D4\u05D0\u05EA\u05E8 \u05DB\u05D3\u05D9 \u05DC\u05D4\u05D5\u05E1\u05D9\u05E3"}</p>
              </div>
            ) : (
              allPagesWithComments.map((page) => {
                let pageComms = comments.filter((c) => c.page === page);
                // Apply panel filter
                if (panelFilter === "open") pageComms = pageComms.filter(c => !c.resolved);
                else if (panelFilter === "resolved") pageComms = pageComms.filter(c => c.resolved);
                if (pageComms.length === 0) return null;
                const pageName = getPageName(page);
                return (
                  <div key={page}>
                    <div className="px-3 py-2 bg-gray-50 border-b border-gray-100">
                      <p className="text-xs text-gray-500" style={{ fontWeight: 600 }}>
                        {pageName} ({pageComms.length})
                      </p>
                    </div>
                    {pageComms.map((comment, idx) => (
                      <div
                        key={comment.id}
                        onClick={() => {
                          if (comment.page === relativePath) {
                            scrollToComment(comment);
                          }
                        }}
                        className={`p-3 border-b border-gray-50 hover:bg-zap-pale/50 ${
                          comment.page === relativePath ? "cursor-pointer" : "opacity-60"
                        } ${activePin === comment.id ? "bg-zap-pale" : ""}`}
                      >
                        <div className="flex items-start gap-2">
                          <div
                            className={`w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-white text-[10px] ${
                              comment.resolved ? "bg-green-500" : comment.mentions && comment.mentions.length > 0 ? "bg-purple-500" : "bg-zap"
                            }`}
                            style={{ fontWeight: 700 }}
                          >
                            {comment.mentions && comment.mentions.length > 0 && !comment.resolved ? (
                              <AtSign className="w-3 h-3" />
                            ) : (
                              idx + 1
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <p className="text-xs text-zap-navy truncate" style={{ fontWeight: 600 }}>{comment.author}</p>
                              <div className="flex items-center gap-1">
                                <span className="text-[10px] text-gray-400">{formatTime(comment.timestamp)}</span>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteComment(comment.id);
                                  }}
                                  className="btnReset p-0.5 text-gray-300 hover:text-red-500"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            </div>
                            <div className={`text-xs text-gray-600 mt-1 line-clamp-2 ${comment.resolved ? "line-through text-gray-400" : ""}`}>
                              {renderTextWithMentions(comment.text)}
                            </div>
                            {/* Reply count + Mention badges in panel */}
                            <div className="flex flex-wrap items-center gap-1 mt-1">
                              {comment.replies && comment.replies.length > 0 && (
                                <span className="text-[9px] bg-blue-50 text-blue-500 rounded px-1 py-0.5 flex items-center gap-0.5">
                                  <Reply className="w-2.5 h-2.5" />
                                  {comment.replies.length}
                                </span>
                              )}
                              {comment.mentions && comment.mentions.length > 0 && comment.mentions.map((m) => (
                                <span key={m} className="text-[9px] bg-purple-50 text-purple-500 rounded px-1 py-0.5">
                                  @{m}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function getPageName(path: string): string {
  const names: Record<string, string> = {
    "/": "\u05D3\u05E3 \u05D4\u05D1\u05D9\u05EA",
    "/about": "\u05D0\u05D5\u05D3\u05D5\u05EA",
    "/services": "\u05E9\u05D9\u05E8\u05D5\u05EA\u05D9\u05DD",
    "/service-a": "\u05E9\u05D9\u05E8\u05D5\u05EA \u05D0\u05F3",
    "/service-b": "\u05E9\u05D9\u05E8\u05D5\u05EA \u05D1\u05F3",
    "/products": "\u05DE\u05D5\u05E6\u05E8\u05D9\u05DD",
    "/product-a": "\u05DE\u05D5\u05E6\u05E8 \u05D0\u05F3",
    "/product-b": "\u05DE\u05D5\u05E6\u05E8 \u05D1\u05F3",
    "/projects": "\u05EA\u05D9\u05E7 \u05E2\u05D1\u05D5\u05D3\u05D5\u05EA",
    "/consulting": "\u05D9\u05D9\u05E2\u05D5\u05E5",
    "/blog": "\u05D1\u05DC\u05D5\u05D2",
    "/contact": "\u05E6\u05D5\u05E8 \u05E7\u05E9\u05E8",
  };
  return names[path] || path;
}